import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	useGetCustomRatesListQuery,
	useGetDetailedDocumentQuery,
	useGetDocumentLogsQuery,
	useGetEnhancedCustomRatesQuery,
	useGetLineItemsQuery,
} from '../api';
import { transformLineItemsFromDB } from '../utils/dataTransformer';

import type { ComparisonStatement, UnifiedAnalysisData } from '../types';
import type { ViewMode } from '../utils/dataTransformer';

// Cap the processing-logs poller so a file stuck in "processing" (e.g. an
// upstream blip that never finalizes — MBB-585) doesn't poll /logs forever.
const LOG_POLL_INTERVAL_MS = 5000;
const MAX_LOG_POLLS = 120; // 120 × 5s ≈ 10 min, then stop and let the user refresh
const PDF_POLL_INTERVAL_MS = 10000;
const MAX_PDF_POLLS = 30; // 30 × 10s = 5 min for the readable copy of a CSV or email

// The UI's FLAT_RATE choice submits to the backend as FLAT_TIERED, and some
// legacy payloads say FLAT — collapse all flat aliases before comparing so a
// detected-vs-selected check never false-triggers on naming alone (ROC-62).
const normalizeRatePlanType = (value?: string | null): string | null => {
	if (!value) return null;
	const upper = value.toUpperCase();
	if (upper === 'FLAT_RATE' || upper === 'FLAT') return 'FLAT_TIERED';
	return upper;
};

interface UseAnalysisDataProps {
	document: {
		id: string;
		processingMonth: string;
		status: string;
		pdfUrl?: string;
	};
	comparisonData?: ComparisonStatement | null;
	enableLineItemsFromDB?: boolean;
	viewMode?: ViewMode;
}

export const useAnalysisData = ({
	document,
	comparisonData,
	enableLineItemsFromDB = false,
	viewMode = 'default',
}: UseAnalysisDataProps) => {
	// Local state for document status and data to prevent full re-renders
	const [localDocumentStatus, setLocalDocumentStatus] = useState(
		document.status,
	);
	const [localComparisonData, setLocalComparisonData] =
		useState(comparisonData);
	const [isLogsCollapsed, setIsLogsCollapsed] = useState(
		document.status === 'completed',
	);
	const [logPollCount, setLogPollCount] = useState(0);

	// Use logs endpoint for both status and logs - single API call.
	// Stop polling after MAX_LOG_POLLS so a file stuck in "processing" doesn't
	// poll /logs forever; the user can still refresh manually via refetchLogs.
	const isLogPollingCapped = logPollCount >= MAX_LOG_POLLS;
	const { data: logsData, refetch: refetchLogs } = useGetDocumentLogsQuery(
		document.id,
		{
			pollingInterval:
				localDocumentStatus === 'processing' && !isLogPollingCapped
					? LOG_POLL_INTERVAL_MS
					: 0,
			refetchOnMountOrArgChange: true,
			// Catch up at once when the tab regains focus or the network returns.
			refetchOnFocus: true,
			refetchOnReconnect: true,
		},
	);

	// Count each completed poll while processing; reset when the document changes.
	useEffect(() => {
		if (logsData?.current_status === 'processing') {
			setLogPollCount((count) => count + 1);
		}
	}, [logsData]);

	useEffect(() => {
		setLogPollCount(0);
	}, [document.id]);

	const [pdfPollCount, setPdfPollCount] = useState(0);
	const [pdfPreparing, setPdfPreparing] = useState(false);
	const {
		data: refreshedDocument,
		refetch,
		isFetching,
		isLoading,
	} = useGetDetailedDocumentQuery(document.id, {
		refetchOnMountOrArgChange: true,
		pollingInterval:
			pdfPreparing && pdfPollCount < MAX_PDF_POLLS ? PDF_POLL_INTERVAL_MS : 0,
	});
	const pdfStatus = refreshedDocument?.signed_urls?.pdf_status;

	useEffect(() => {
		setPdfPreparing(pdfStatus === 'preparing');
	}, [refreshedDocument, pdfStatus]);

	// Count every finished poll, failed ones too, so an erroring /detailed still stops.
	useEffect(() => {
		if (!isFetching && pdfPreparing) setPdfPollCount((count) => count + 1);
	}, [isFetching, pdfPreparing]);

	useEffect(() => {
		setPdfPollCount(0);
	}, [document.id]);

	// Fetch default config list (only if custom_rates is null)
	const customRatesFromDoc = (refreshedDocument as any)?.results
		?.processing_metadata?.custom_rates;
	const selectedRiskTierFromDoc =
		(refreshedDocument as any)?.results?.processing_metadata
			?.selected_risk_tier ||
		(refreshedDocument as any)?.results?.revenue_analysis?.risk_tier;

	// Check if customRatesFromDoc is in a format the modal can use
	const isCustomRatesUsable =
		customRatesFromDoc &&
		(customRatesFromDoc.custom_rates_id || // Has reference to stored config
			customRatesFromDoc.rate_plan_type || // Has enhanced format
			customRatesFromDoc.rates); // Has enhanced format

	const { data: configsResponse } = useGetCustomRatesListQuery(
		{ page: 1, page_size: 50 },
		{ skip: isCustomRatesUsable }, // Only skip if we have usable custom_rates
	);

	// Get default config ID
	const defaultConfigId = useMemo(() => {
		if (configsResponse?.data) {
			const defaultConfig = configsResponse.data.find(
				(config: any) => config.is_default,
			);
			return defaultConfig?.id;
		}
		return undefined;
	}, [configsResponse]);

	// Fetch enhanced config if we have a default config ID (only if custom_rates is not usable)
	const { data: enhancedConfigResponse } = useGetEnhancedCustomRatesQuery(
		defaultConfigId || '',
		{ skip: isCustomRatesUsable || !defaultConfigId },
	);

	// Update local state when logs data changes (includes status)
	useEffect(() => {
		if (logsData) {
			const newStatus = logsData.current_status;
			setLocalDocumentStatus(newStatus);

			// Update logs collapsed state based on status
			if (newStatus === 'processing') {
				setIsLogsCollapsed(false); // Show logs when processing
			} else if (newStatus === 'completed') {
				// If status changed to completed, fetch full data for results and collapse logs
				if (localDocumentStatus === 'processing') {
					refetch();
					// Collapse logs when processing completes
					setTimeout(() => {
						setIsLogsCollapsed(true);
					}, 2000); // Small delay for user to see completion
				} else {
					setIsLogsCollapsed(true); // Keep collapsed if already completed
				}
			} else if (newStatus === 'failed') {
				// A run that just failed may carry a document check: fetch it.
				if (localDocumentStatus === 'processing') {
					refetch();
				}
				// Keep the logs open: the failure reason lives in them, and the
				// header above them offers the rerun.
				setIsLogsCollapsed(false);
			} else {
				setIsLogsCollapsed(true); // Collapse for other statuses
			}
		}
	}, [logsData, localDocumentStatus, refetch]);

	// Update local state when the results or status change (not on a PDF poll)
	const refreshedResults = refreshedDocument?.results;
	const refreshedStatus = refreshedDocument?.current_status;
	useEffect(() => {
		if (refreshedDocument) {
			setLocalDocumentStatus(refreshedDocument.current_status);

			// Update comparison data if available - handle multiple data paths gracefully
			let newComparisonData = null;

			if (refreshedDocument.results?.cost_comparison?.comparison_data) {
				newComparisonData =
					refreshedDocument.results.cost_comparison.comparison_data;
			} else if (refreshedDocument.results?.cost_comparison) {
				newComparisonData = refreshedDocument.results.cost_comparison;
			} else if (refreshedDocument.results?.comparison_data) {
				// Flat tiered statements have comparison_data directly under results
				newComparisonData = refreshedDocument.results.comparison_data;
			}

			// Attach revenue_analysis from top-level results to comparison data
			if (newComparisonData && refreshedDocument.results?.revenue_analysis) {
				newComparisonData = {
					...newComparisonData,
					revenue_analysis: refreshedDocument.results.revenue_analysis,
				};
			}

			setLocalComparisonData(newComparisonData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshedResults, refreshedStatus]);

	// Get analysis_type from refreshedDocument (check results first for recalculation type)
	const currentAnalysisType =
		refreshedDocument?.results?.analysis_type ||
		refreshedDocument?.analysis_type;

	// Get action_type from refreshedDocument
	const currentActionType = refreshedDocument?.action_type;

	// Check if this is a rerun using action_type field
	const isRerun = currentActionType === 'rerun';

	// ROC-62: statement-type mismatch (classifier-detected vs user-selected).
	// Only meaningful once processing finished — while processing, the detected
	// type may not be populated yet.
	const selectedRatePlanType = refreshedDocument?.rate_plan_type ?? null;
	const detectedRatePlanType =
		refreshedDocument?.detected_rate_plan_type ?? null;
	const hasTypeMismatch = useMemo(() => {
		const detected = normalizeRatePlanType(detectedRatePlanType);
		const selected = normalizeRatePlanType(selectedRatePlanType);
		return Boolean(
			localDocumentStatus === 'completed' &&
			detected &&
			selected &&
			detected !== selected,
		);
	}, [detectedRatePlanType, selectedRatePlanType, localDocumentStatus]);

	// Line items from database - always fetch when document is completed
	// This is the single source of truth for rendering
	const lineItemsQueryResult = useGetLineItemsQuery(
		{ fileId: document.id, viewMode },
		{
			skip: localDocumentStatus !== 'completed',
			// Refetch on mount and when args change to ensure fresh data
			refetchOnMountOrArgChange: true,
		},
	);
	const lineItemsData = lineItemsQueryResult.data;
	const refetchLineItems = lineItemsQueryResult.refetch;
	const isFetchingLineItems = lineItemsQueryResult.isFetching;

	// Determine custom_rates to use
	// Priority: 1) custom_rates from line items API (last-used values), 2) document processing_metadata, 3) default config
	const customRates = useMemo(() => {
		// First priority: Use custom_rates directly from line items API (most accurate - shows last-used values)
		if (lineItemsData?.custom_rates) {
			const rates = {
				...lineItemsData.custom_rates,
				rate_plan_type:
					lineItemsData.rate_plan_type ||
					lineItemsData.custom_rates.rate_plan_type,
			};

			// Add selected_risk_tier if available
			if (selectedRiskTierFromDoc) {
				return {
					...rates,
					processing_metadata: {
						selected_risk_tier: selectedRiskTierFromDoc,
					},
					revenue_analysis: {
						risk_tier: selectedRiskTierFromDoc,
					},
				};
			}

			return rates;
		}

		// Fallback: If we have custom_rates from the document, use it
		if (customRatesFromDoc) {
			// Check if it has custom_rates_id (reference to stored config)
			if (customRatesFromDoc.custom_rates_id) {
				// Return minimal object with just the ID for modal to fetch via API
				const rates = {
					custom_rates_id: customRatesFromDoc.custom_rates_id,
					name: customRatesFromDoc.name,
				};
				if (selectedRiskTierFromDoc) {
					return {
						...rates,
						processing_metadata: {
							selected_risk_tier: selectedRiskTierFromDoc,
						},
						revenue_analysis: {
							risk_tier: selectedRiskTierFromDoc,
						},
					};
				}
				return rates;
			}
			// Check if it's in enhanced format (has rate_plan_type or rates)
			else if (customRatesFromDoc.rate_plan_type || customRatesFromDoc.rates) {
				// It's the full direct_rates config in enhanced format
				const rates = customRatesFromDoc;
				if (selectedRiskTierFromDoc) {
					return {
						...rates,
						processing_metadata: {
							selected_risk_tier: selectedRiskTierFromDoc,
						},
						revenue_analysis: {
							risk_tier: selectedRiskTierFromDoc,
						},
					};
				}
				return rates;
			}
			// Legacy format (has card network keys) - fall back to default config
			else {
				const rates = enhancedConfigResponse?.data || null;
				if (rates && selectedRiskTierFromDoc) {
					return {
						...rates,
						processing_metadata: {
							selected_risk_tier: selectedRiskTierFromDoc,
						},
						revenue_analysis: {
							risk_tier: selectedRiskTierFromDoc,
						},
					};
				}
				return rates;
			}
		}
		// Final fallback: use the enhanced default config
		else if (enhancedConfigResponse?.data) {
			const rates = enhancedConfigResponse.data;
			if (selectedRiskTierFromDoc) {
				return {
					...rates,
					processing_metadata: {
						selected_risk_tier: selectedRiskTierFromDoc,
					},
					revenue_analysis: {
						risk_tier: selectedRiskTierFromDoc,
					},
				};
			}
			return rates;
		}

		return null;
	}, [
		lineItemsData,
		customRatesFromDoc,
		enhancedConfigResponse,
		selectedRiskTierFromDoc,
	]);

	// Refetch line items when viewMode changes
	useEffect(() => {
		if (localDocumentStatus === 'completed' && refetchLineItems) {
			refetchLineItems();
		}
	}, [viewMode, localDocumentStatus, refetchLineItems]);

	// Transform database line items into unified format
	// Only use database line items - no fallback to processing_results
	const analysisData: UnifiedAnalysisData = useMemo(() => {
		// Use database line items if available
		if (lineItemsData?.line_items && lineItemsData.line_items.length > 0) {
			try {
				const dbAnalysisData = transformLineItemsFromDB(
					lineItemsData.line_items,
					document.processingMonth,
					customRates?.name,
					viewMode,
				);

				// Attach revenue_analysis from refreshedDocument if available
				if (refreshedDocument?.results?.revenue_analysis) {
					return {
						...dbAnalysisData,
						revenue_analysis: refreshedDocument.results.revenue_analysis,
					};
				}

				return dbAnalysisData;
			} catch (error) {
				console.error('Error transforming database line items:', error);
			}
		}

		// Return empty data if database line items not available
		return {
			processingMonth: document.processingMonth || 'Unknown',
			duesFeesAssessments: [],
			interchange: [],
			authorizationAndDiscount: [],
			otherFees: [],
			processorName: '',
			processorNamePlus: '',
			totals: {
				ic: 0,
				icPlus: 0,
			},
		};
	}, [
		lineItemsData?.line_items,
		document.processingMonth,
		refreshedDocument?.results?.revenue_analysis,
		customRates,
		viewMode,
	]);

	// Use backend-calculated totals when available (statement-type-aware)
	// Falls back to frontend calculation for backwards compatibility
	const calculatedTotals = useMemo(() => {
		const backendTotals = lineItemsData?.calculated_totals;

		if (backendTotals) {
			return {
				ic: backendTotals.current_processor.grand_total,
				icPlus: backendTotals.comparison_processor.grand_total,
				savings: backendTotals.savings,
				effectiveRates: {
					current: backendTotals.current_processor.effective_rate,
					comparison: backendTotals.comparison_processor.effective_rate,
				},
				volume: backendTotals.current_processor.volume,
				calculationMethod: backendTotals.calculation_method,
				show_rate_disclaimer: backendTotals.show_rate_disclaimer,
				totalSource: backendTotals.current_processor.total_source as
					| 'document'
					| 'calculated'
					| undefined,
				documentTotal: backendTotals.document_total as
					| { amount: number; label: string; page_number?: number }
					| undefined,
			};
		}

		// Fallback to frontend-calculated totals (backwards compatibility)
		return {
			ic: analysisData?.totals?.ic ?? 0,
			icPlus: analysisData?.totals?.icPlus ?? 0,
			savings: null,
			effectiveRates: null,
			volume: null,
			calculationMethod: null,
			show_rate_disclaimer: false,
			totalSource: undefined,
			documentTotal: undefined,
		};
	}, [
		lineItemsData?.calculated_totals,
		analysisData?.totals?.ic,
		analysisData?.totals?.icPlus,
	]);

	// Revenue analysis: prefer line-items API (reactive to edits) over processing_results (static)
	// This ensures ISO Profit updates when user edits rates in the analysis grid
	const revenueAnalysis = useMemo(() => {
		// First priority: fresh calculation from line items API (updates on every edit)
		if (lineItemsData?.revenue_analysis) {
			return lineItemsData.revenue_analysis;
		}
		// Fallback: static revenue_analysis from processing_results (initial processing)
		if (refreshedDocument?.results?.revenue_analysis) {
			return refreshedDocument.results.revenue_analysis;
		}
		return null;
	}, [
		lineItemsData?.revenue_analysis,
		refreshedDocument?.results?.revenue_analysis,
	]);

	const monthlySavingsData = useMemo(() => {
		// Prefer backend-calculated savings when available
		if (calculatedTotals.savings) {
			return {
				value: calculatedTotals.savings.monthly,
				isNegative: calculatedTotals.savings.is_negative,
			};
		}

		// Fallback to frontend calculation
		const ic = calculatedTotals.ic;
		const icPlus = calculatedTotals.icPlus;
		const monthlySavings = ic - icPlus;

		return {
			value: monthlySavings,
			isNegative: monthlySavings < 0,
		};
	}, [calculatedTotals]);

	// Create enhanced document object with PDF URL for DocumentViewer
	const enhancedDocument = useMemo(() => {
		const pdfUrl = refreshedDocument?.signed_urls?.pdf_url || document.pdfUrl;
		const signedUrls = refreshedDocument?.signed_urls;
		return {
			...document,
			pdfUrl,
			originalUrl: signedUrls?.original_url,
			originalType: signedUrls?.original_content_type,
			originalName: signedUrls?.original_filename,
			pdfStatus: signedUrls?.pdf_status,
			pdfPollingStopped: pdfPreparing && pdfPollCount >= MAX_PDF_POLLS,
			status: localDocumentStatus,
		};
	}, [
		document,
		refreshedDocument,
		localDocumentStatus,
		pdfPreparing,
		pdfPollCount,
	]);

	// Show progress/loading state when processing and no comparison data available
	const showProgress =
		localDocumentStatus === 'processing' && !localComparisonData;

	// Callback to refresh line items after edit
	// Only refetch line items - do NOT call detailed API to avoid rerendering
	const handleLineItemUpdated = useCallback(async () => {
		// Force refetch to get fresh data after update
		// RTK Query's invalidatesTags should trigger this automatically,
		// but we also call refetch explicitly to ensure it happens
		if (localDocumentStatus === 'completed') {
			// Refetch will work even if query was previously skipped
			const result = await refetchLineItems();
		}
		// Do NOT call refetch() - we only use line items from database, not processing_results
	}, [refetchLineItems, localDocumentStatus]);

	return {
		localDocumentStatus,
		localComparisonData,
		isLogsCollapsed,
		setIsLogsCollapsed,
		logsData,
		refetchLogs,
		logPollingStopped:
			localDocumentStatus === 'processing' && isLogPollingCapped,
		refreshedDocument,
		refetch,
		isFetching,
		isLoading,
		analysisData,
		monthlySavingsData,
		calculatedTotals, // Backend-calculated totals (statement-type-aware)
		revenueAnalysis, // ISO Profit - reactive to line item edits
		enhancedDocument,
		showProgress,
		customRates,
		analysisType: currentAnalysisType,
		actionType: currentActionType,
		// Statement-type mismatch (ROC-62)
		selectedRatePlanType,
		detectedRatePlanType,
		hasTypeMismatch,
		// Line item editing support
		lineItemsData,
		refetchLineItems,
		isFetchingLineItems,
		handleLineItemUpdated,
		enableEditing: enableLineItemsFromDB,

		// View mode helpers
		hasUserEdits: useMemo(() => {
			if (!lineItemsData?.line_items) return false;
			return lineItemsData.line_items.some((item) => {
				const userEdits = item.extra_attributes?.user_edits || {};
				return Boolean(userEdits.ic_data || userEdits.ic_plus_data);
			});
		}, [lineItemsData?.line_items]),

		hasCustomizedQuote: useMemo(() => {
			if (!lineItemsData?.line_items) return false;
			return lineItemsData.line_items.some((item) => {
				return Boolean(item.extra_attributes?.customized_quote);
			});
		}, [lineItemsData?.line_items]),
	};
};
